import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Box,
  Grid,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Button,
  Typography,
  Paper,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  loginStart,
  loginSuccess,
  loginFailure,
  selectIsLoading,
  selectError,
  clearError
} from '../redux/userSlice';
import { authAPI } from '../utils/Url';
import logo from '../assets/images/qq.png'; // Update path as needed
import BgImg1 from './images/bgVTS1.png'; // Update path as needed

const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoading = useSelector(selectIsLoading);
  const error = useSelector(selectError);

  // Clear error on component unmount
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  // Show error toast when error changes
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Form validation schema
  const validationSchema = Yup.object({
    userName: Yup.string()
      .required('Username is required')
      .min(3, 'Username must be at least 3 characters'),
    password: Yup.string()
      .required('Password is required')
      .min(6, 'Password must be at least 6 characters'),
  });

  // Formik form
  const formik = useFormik({
    initialValues: {
      userName: '',
      password: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      dispatch(loginStart());

      try {
        const response = await authAPI.login(values.userName, values.password);

        if (response?.isSuccess) {
          const { token, refreshtoken, expiration, data: userData } = response.data;

          dispatch(loginSuccess({
            userDetails: {
              userName: userData.userName,
              phoneNumber: userData.phoneNumber,
              email: userData.email,
              emailConfirmed: userData.emailConfirmed,
            },
            token,
            refreshToken: refreshtoken,
            expiration,
            status: response.code,
          }));

          toast.success(response.mesg || 'Login successful!');
          navigate('/home');
        } else {
          dispatch(loginFailure(response?.mesg || 'Login failed'));
        }
      } catch (error: any) {
        const errorMessage = error?.mesg || error?.message || 'Login failed. Please try again.';
        dispatch(loginFailure(errorMessage));
      }
    },
  });

  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !isLoading) {
      formik.handleSubmit();
    }
  };

  return (
    <Grid
      container
      sx={{
        height: '100vh',
        backgroundImage: `url(${BgImg1})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Grid item xs={12} sm={8} md={6} lg={4}>
        <Paper
          elevation={24}
          sx={{
            padding: 4,
            borderRadius: '2rem',
            border: '4px solid #fff',
            backgroundColor: '#91a68a',
            opacity: 0.9,
            textAlign: 'center',
          }}
        >
          {/* Logo */}
          <Box sx={{ mb: 3 }}>
            <img
              src={logo}
              alt="Logo"
              style={{
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                objectFit: 'cover',
              }}
            />
          </Box>

          {/* Title */}
          <Typography
            variant="h4"
            sx={{
              color: '#fff',
              fontWeight: 'bold',
              mb: 1,
              fontFamily: 'Georgia, serif',
            }}
          >
            <span style={{ color: '#FF671F', fontSize: '2.5rem' }}>V</span>
            <span style={{ color: '#000' }}>ehicle </span>
            <span style={{ color: '#D50E00', fontSize: '2.5rem' }}>T</span>
            <span style={{ color: '#000' }}>racking </span>
            <span style={{ color: '#FFE100', fontSize: '2.5rem' }}>S</span>
            <span style={{ color: '#000' }}>ystem</span>
          </Typography>

          <Typography
            variant="subtitle2"
            sx={{
              color: '#000',
              mb: 4,
              fontWeight: 'bold',
              fontStyle: 'italic',
            }}
          >
            "Engage & Analyze: Your Tracking Made Simple!"
          </Typography>

          {/* Login Form */}
          <form onSubmit={formik.handleSubmit} onKeyPress={handleKeyPress}>
            {/* Username Field */}
            <TextField
              fullWidth
              id="userName"
              name="userName"
              label="Username"
              variant="outlined"
              value={formik.values.userName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.userName && Boolean(formik.errors.userName)}
              helperText={formik.touched.userName && formik.errors.userName}
              disabled={isLoading}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '3rem',
                  backgroundColor: '#fff',
                },
              }}
            />

            {/* Password Field */}
            <TextField
              fullWidth
              id="password"
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              disabled={isLoading}
              sx={{
                mb: 4,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '3rem',
                  backgroundColor: '#fff',
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleShowPassword}
                      edge="end"
                      disabled={isLoading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Login Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{
                borderRadius: '3rem',
                padding: '12px',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                backgroundColor: '#003380',
                '&:hover': {
                  backgroundColor: '#002244',
                },
                '&:disabled': {
                  backgroundColor: '#cccccc',
                },
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Login'
              )}
            </Button>
          </form>

          {/* Error Display */}
          {formik.submitCount > 0 && !isLoading && (
            <Typography
              color="error"
              sx={{
                mt: 2,
                fontSize: '0.875rem',
                fontWeight: 'bold',
              }}
            >
              {error}
            </Typography>
          )}
        </Paper>
      </Grid>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </Grid>
  );
};

export default LoginPage;